import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'
import ReleaseShowController from '@/router/App/Http/Controllers/Release/ReleaseShowController'

/** How often an open page checks for a new release. */
export const RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000

/** Coming back to the tab checks again, but not more often than this. */
export const RELEASE_RECHECK_MIN_MS = 60 * 1000

export interface ReleaseWatcher {
  /** A newer release is running than the one this page loaded. */
  updateAvailable: Ref<boolean>
  /** Hides the prompt until another release comes out. */
  dismiss: () => void
  refresh: () => void
  /** Checks right away. Exposed for tests. */
  check: () => Promise<void>
}

/** The release the page was served with, from the `app-release` meta tag. */
export const loadedRelease = (): string | null =>
  document.head.querySelector<HTMLMetaElement>('meta[name="app-release"]')?.content || null

/**
 * Notices when a deploy replaces the code a page was loaded with.
 *
 * It checks every few minutes and whenever the tab comes back into view. A
 * failed check, as can happen mid-deploy, is ignored and tried again later.
 */
export function useReleaseWatcher(): ReleaseWatcher {
  const updateAvailable = ref(false)
  const current = loadedRelease()
  let dismissedRelease: string | null = null
  let latestRelease: string | null = null
  let lastCheckedAt = 0
  let timer: ReturnType<typeof setInterval> | null = null

  const check = async (): Promise<void> => {
    if (!current) return
    lastCheckedAt = Date.now()

    try {
      const response = await fetch(ReleaseShowController.url(), {
        cache: 'no-store',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return

      const body = (await response.json()) as { release?: unknown }
      if (typeof body.release !== 'string' || !body.release) return

      latestRelease = body.release
      updateAvailable.value = latestRelease !== current && latestRelease !== dismissedRelease
    } catch {
      // Offline or mid-deploy; the next check will try again.
    }
  }

  const onVisible = (): void => {
    if (
      document.visibilityState === 'visible' &&
      Date.now() - lastCheckedAt >= RELEASE_RECHECK_MIN_MS
    ) {
      void check()
    }
  }

  const dismiss = (): void => {
    dismissedRelease = latestRelease
    updateAvailable.value = false
  }

  const refresh = (): void => {
    window.location.reload()
  }

  onMounted(() => {
    if (!current) return
    lastCheckedAt = Date.now()
    timer = setInterval(() => void check(), RELEASE_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
  })

  onBeforeUnmount(() => {
    if (timer) clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
  })

  return { updateAvailable, dismiss, refresh, check }
}
